import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  Subject,
} from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { generateClient } from 'aws-amplify/api';
import { Store } from '@ngxs/store';
import { AuthenticateUserState } from '../store/authenticate-user.state';
import { CustomToastrService } from '../../shared/ngx-toastr/custom-toastr.service';
import {
  ActiveJobMarker,
  JOB_TYPE_PC_RESET,
  JobProgressEvent,
  JobProgressState,
} from '../models/job-progress';
import { onJobProgress } from '../../../awsAppSync/graphql/subscriptions';
import { getJobProgress } from '../../../awsAppSync/graphql/queries';
import {
  GetJobProgressQuery,
  OnJobProgressSubscription,
} from '../../../awsAppSync/API';
import {
  JOB_PROGRESS_ACTIVE_JOBS_KEY,
  JOB_PROGRESS_DEFAULT_PHASE,
  JOB_PROGRESS_RESEED_ATTEMPTS,
  JOB_PROGRESS_RESEED_INTERVAL_MS,
  JOB_PROGRESS_USERID_RESOLVE_TIMEOUT_MS,
  JOB_PROGRESS_WATCHDOG_INTERVAL_MS,
  JOB_PROGRESS_STATUS_FAILED,
  JOB_PROGRESS_STATUS_SUCCESS,
  JOB_PROGRESS_TOAST_FAILED,
  JOB_PROGRESS_TOAST_SUCCESS,
  TOASTER_SUCCESS,
  ERROR,
} from '../../shared/constants';

/**
 * SYRC-7180: shared, user-scoped, multi-job progress tracker. ONE
 * `onJobProgress(userId)` subscription for the whole session; incoming events
 * are demuxed by `jobId` so concurrent jobs never overwrite each other.
 *
 * Lifecycle:
 *  - init(userId) at app-shell / login: opens the shared subscription and
 *    re-seeds any jobs left active in the reload-survival marker.
 *  - track(jobId, jobType, label): start watching a job (seed via GetJobProgress,
 *    write the active-job marker); live updates arrive on the shared subscription.
 *  - watch(jobId): per-job state stream for a card/toast.
 *
 * `percent` is monotonic + clamped 0-100 server-side; we still guard defensively.
 */
@Injectable({ providedIn: 'root' })
export class JobProgressService {
  private readonly client = generateClient();

  // Per-job state streams, demuxed by jobId.
  private readonly jobs = new Map<string, BehaviorSubject<JobProgressState>>();

  // Aggregate of all live jobs, for the app-shell UI (modal + toasts).
  private readonly activeJobsSubject = new BehaviorSubject<JobProgressState[]>(
    [],
  );
  readonly activeJobs$: Observable<JobProgressState[]> =
    this.activeJobsSubject.asObservable();

  private readonly terminalSubject = new Subject<JobProgressState>();
  /** Emits once per job when it reaches Success/Failed. */
  readonly terminal$: Observable<JobProgressState> =
    this.terminalSubject.asObservable();

  // The single shared subscription handle for this session.
  private subscription: { unsubscribe: () => void } | null = null;
  private subscribedUserId: string | null = null;

  // Bounded confirmation re-seed timers, keyed by jobId, so a fast job that
  // finished during the connect window is recovered via getJobProgress even when
  // no live event was received. Cleared on terminal/dismiss.
  private readonly reseedTimers = new Map<string, ReturnType<typeof setTimeout>>();

  // SYRC-7421: slow re-check for long-running reset jobs, keyed by jobId. Unlike the bounded re-seed it
  // is NOT stopped by live events: a dropped connection or a missed terminal event must still end.
  private readonly watchdogTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  // jobIds that have received at least one LIVE subscription event. Once a job is
  // getting live pushes, the re-seed net backs off (live is the source of truth).
  private readonly liveJobs = new Set<string>();

  constructor(
    private store: Store,
    private toastr: CustomToastrService,
  ) {}

  /**
   * App-shell init / post-login. Opens the shared subscription (idempotent) and
   * re-seeds any jobs that were active before a reload so their toasts resume.
   */
  init(userId?: string | null): void {
    const uid = userId ?? this.getUserId();
    if (!uid) {
      return;
    }
    const markers = this.readMarkers();
    // Nothing to recover -> do NOT open the subscription. The shared socket is
    // opened lazily: at track() when a job actually starts, or here only when a
    // reload left active-job markers to resume. A plain page load with no job in
    // flight must not open (and fail) the connection.
    if (markers.length === 0) {
      return;
    }
    this.openSubscription(uid);
    for (const marker of markers) {
      // Recovery: seed each marked job using the marker's OWN userId (the id that
      // originally owned the job), so recovery is correct even if the resolved
      // session id differs. Skip markers with no userId (legacy/corrupt) rather
      // than seeding with the wrong owner. A null query result means the job is
      // genuinely gone (finished while away) -> prune it.
      if (!marker.userId) {
        this.removeMarker(marker.jobId);
        continue;
      }
      void this.seed(
        marker.userId,
        marker.jobId,
        marker.jobType,
        marker.label,
        marker.minimized,
        true,
      ).then(() => {
        // If recovery seeded a still-running job, arm the bounded re-seed so it
        // still reaches terminal even if its remaining live events are missed.
        const subject = this.jobs.get(marker.jobId);
        if (subject && !this.isTerminal(subject.value.status)) {
          this.scheduleReseed(
            marker.userId,
            marker.jobId,
            marker.jobType,
            marker.label,
            JOB_PROGRESS_RESEED_ATTEMPTS,
          );
          this.armWatchdog(
            marker.userId,
            marker.jobId,
            marker.jobType,
            marker.label,
          );
        }
      });
    }
  }

  /**
   * Start watching a job. Seeds current progress via the query, writes the
   * active-job marker, and relies on the shared subscription for live updates.
   * Call right after a job-starting mutation returns its ack.
   */
  async track(jobId: string, jobType: string, label: string): Promise<void> {
    if (!jobId) {
      return;
    }
    // Show the optimistic 0% card immediately, keyed by jobId, so the UI appears
    // the instant the delete is confirmed — even while the SYRC user id is still
    // resolving. This is safe: it is demuxed by jobId and superseded by the seed.
    this.upsert(
      { userId: '', jobId, jobType, status: 'InProgress', percent: 0 },
      label,
    );

    // Resolve the SYRC user id. The subscription is keyed on it (post-refactor),
    // and it can be '' during the cold-login window — so WAIT for it rather than
    // silently dropping the job. Bounded, so a genuinely-never-resolving id can't
    // hang the tracker.
    const uid = await this.resolveUserId();
    if (!uid) {
      console.warn(
        '[JobProgress] track() — SYRC userId did not resolve; cannot attach subscription',
      );
      return;
    }

    // Order matters: OPEN THE SUBSCRIPTION FIRST, then seed. Opening first means
    // any event published after connect is captured; seeding after closes the gap
    // before connect. (The subscription is shared/idempotent per user.)
    this.openSubscription(uid);
    this.writeMarker({ userId: uid, jobId, jobType, label, minimized: false });
    // Fresh job: a null query result is expected before the first tick, so keep
    // the optimistic 0% state (don't prune). The bounded re-seed below recovers
    // the terminal state for fast jobs whose live events were missed.
    void this.seed(uid, jobId, jobType, label, false, false);
    this.scheduleReseed(uid, jobId, jobType, label, JOB_PROGRESS_RESEED_ATTEMPTS);
    this.armWatchdog(uid, jobId, jobType, label);
  }

  /**
   * Resolves the SYRC user id used as the subscription/publish key. Returns it
   * immediately when already present; otherwise waits (bounded) for the store to
   * emit a non-empty value — covering the cold-login window where a delete can fire
   * before the profile id has landed. Returns '' if it never resolves in time.
   */
  private async resolveUserId(): Promise<string> {
    const immediate = this.getUserId();
    if (immediate) {
      return immediate;
    }
    try {
      return await firstValueFrom(
        this.store.select(AuthenticateUserState.getSyrcUserId).pipe(
          filter((id): id is string => !!id),
          take(1),
          timeout(JOB_PROGRESS_USERID_RESOLVE_TIMEOUT_MS),
        ),
      );
    } catch {
      return '';
    }
  }

  /** Per-job state stream for a card/toast. Creates an empty entry if unknown. */
  watch(jobId: string): Observable<JobProgressState> {
    return this.ensureSubject(jobId).asObservable();
  }

  /**
   * "Run in background": collapse a job's modal into the toast. Presentation
   * only — the shared subscription keeps running. Persisted so a reload keeps it.
   */
  minimize(jobId: string): void {
    const subject = this.jobs.get(jobId);
    const current = subject?.value;
    if (subject && current && !current.minimized) {
      subject.next({ ...current, minimized: true });
      this.updateMarker(jobId, (m) => ({ ...m, minimized: true }));
      this.emitActiveJobs();
    }
  }

  /**
   * User closed the toast. Removes it from the UI: completes and prunes the job's
   * stream, clears its reload marker, and re-emits the active list. This does NOT
   * cancel the underlying job (PC deletion can't be cancelled) — it only stops the
   * client from showing/recovering it. If the job later completes the terminal
   * toast still fires from the shared subscription while the session lives; after a
   * reload the (removed) marker means it simply won't reappear.
   */
  dismiss(jobId: string): void {
    const subject = this.jobs.get(jobId);
    if (!subject) {
      return;
    }
    this.cancelReseed(jobId);
    this.cancelWatchdog(jobId);
    this.liveJobs.delete(jobId);
    this.removeMarker(jobId);
    subject.complete();
    this.jobs.delete(jobId);
    this.emitActiveJobs();
  }

  /** Optional: all live jobs of a given type (e.g. for a type-specific list). */
  jobsByType(jobType: string): JobProgressState[] {
    return [...this.jobs.values()]
      .map((s) => s.value)
      .filter((s) => s.jobType === jobType);
  }

  // --- shared subscription ---
  private openSubscription(userId: string): void {
    // Non-empty guard: onJobProgress(userId: String!) is non-nullable and AppSync
    // rejects the WebSocket connect with UnauthorizedException (before the Lambda
    // authorizer even runs) for a null/empty id. During the cold-login window the
    // user id can still be '' — never open the socket until it is resolved.
    if (!userId) {
      console.warn('[JobProgress] openSubscription aborted — empty userId');
      return;
    }
    if (this.subscription && this.subscribedUserId === userId) {
      return; // one per user for the whole session
    }
    this.teardownSubscription();
    this.subscribedUserId = userId;

    const idToken = this.getIdToken();
    const observable = this.client.graphql({
      query: onJobProgress,
      variables: { userId },
      authToken: idToken,
    }) as { subscribe: (observer: unknown) => { unsubscribe: () => void } };

    this.subscription = observable.subscribe({
      next: (response: { data?: OnJobProgressSubscription }) => {
        const event = response?.data?.onJobProgress;
        console.log(
          '[JobProgress] LIVE event',
          JSON.stringify(event),
          'tracked:',
          [...this.jobs.keys()],
        );
        if (!event?.jobId) {
          return;
        }
        // Only apply to jobs we're tracking (label + jobId demux live client-side).
        const known = this.jobs.get(event.jobId);
        if (known) {
          // A live event has arrived for this job — back off the re-seed net; the
          // subscription is now the source of truth.
          this.liveJobs.add(event.jobId);
          this.cancelReseed(event.jobId);
          this.upsert(event, known.value.label);
        }
      },
      error: (error: unknown) => {
        console.error('onJobProgress subscription error', error);
        // Reset the connection state so a later track()/init() can retry cleanly
        // rather than being blocked by the "one per user" guard pointing at a
        // dead/failed subscription.
        this.subscription = null;
        this.subscribedUserId = null;
      },
    });
  }

  /**
   * Bounded confirmation re-seed: re-query getJobProgress up to `attemptsLeft`
   * times (spaced by JOB_PROGRESS_RESEED_INTERVAL_MS) to recover the final state of
   * a job whose live events were missed during the connect window (e.g. a ~2s job).
   * Stops immediately once the job is terminal, has started receiving live events,
   * or was dismissed/pruned. This is a bounded net for the connect race — NOT an
   * open-ended polling loop.
   */
  private scheduleReseed(
    userId: string,
    jobId: string,
    jobType: string,
    label: string,
    attemptsLeft: number,
  ): void {
    this.cancelReseed(jobId);
    if (attemptsLeft <= 0) {
      return;
    }
    const timer = setTimeout(() => {
      this.reseedTimers.delete(jobId);
      console.log(
        '[JobProgress] re-seed firing for jobId',
        JSON.stringify(jobId),
        'attemptsLeft:',
        attemptsLeft,
      );
      // Stop if the job is gone (terminal/dismissed) or is now driven by live events.
      const subject = this.jobs.get(jobId);
      if (!subject || this.liveJobs.has(jobId)) {
        return;
      }
      if (this.isTerminal(subject.value.status)) {
        return;
      }
      // Re-seed once; seed() applies the row (incl. a terminal Success/100) if present.
      void this.seed(userId, jobId, jobType, label, false, false).finally(() => {
        const still = this.jobs.get(jobId);
        if (still && !this.isTerminal(still.value.status) && !this.liveJobs.has(jobId)) {
          this.scheduleReseed(userId, jobId, jobType, label, attemptsLeft - 1);
        }
      });
    }, JOB_PROGRESS_RESEED_INTERVAL_MS);
    this.reseedTimers.set(jobId, timer);
  }

  /**
   * SYRC-7421: slow, open-ended re-check for company data reset jobs only (delete keeps its bounded
   * behaviour). Re-queries getJobProgress every JOB_PROGRESS_WATCHDOG_INTERVAL_MS until the job is
   * terminal, dismissed or pruned, so a dropped subscription or missed terminal event still reaches an
   * outcome. seed() applies a terminal row through the normal path, which also stops this timer.
   */
  private armWatchdog(
    userId: string,
    jobId: string,
    jobType: string,
    label: string,
  ): void {
    if (jobType !== JOB_TYPE_PC_RESET) {
      return;
    }
    this.cancelWatchdog(jobId);
    const timer = setTimeout(() => {
      this.watchdogTimers.delete(jobId);
      const subject = this.jobs.get(jobId);
      if (!subject || this.isTerminal(subject.value.status)) {
        return;
      }
      void this.seed(userId, jobId, jobType, label, undefined, false).finally(
        () => {
          const still = this.jobs.get(jobId);
          if (still && !this.isTerminal(still.value.status)) {
            this.armWatchdog(userId, jobId, jobType, label);
          }
        },
      );
    }, JOB_PROGRESS_WATCHDOG_INTERVAL_MS);
    this.watchdogTimers.set(jobId, timer);
  }

  private cancelWatchdog(jobId: string): void {
    const timer = this.watchdogTimers.get(jobId);
    if (timer) {
      clearTimeout(timer);
      this.watchdogTimers.delete(jobId);
    }
  }

  private cancelReseed(jobId: string): void {
    const timer = this.reseedTimers.get(jobId);
    if (timer) {
      clearTimeout(timer);
      this.reseedTimers.delete(jobId);
    }
  }

  private async seed(
    userId: string,
    jobId: string,
    jobType: string,
    label: string,
    minimized: boolean | undefined,
    pruneIfMissing: boolean,
  ): Promise<void> {
    const idToken = this.getIdToken();
    try {
      const response = (await this.client.graphql({
        query: getJobProgress,
        variables: { userId, jobId },
        authToken: idToken,
      })) as { data?: GetJobProgressQuery };
      const seedEvent = response?.data?.getJobProgress;
      console.log(
        '[JobProgress] seed() result for jobId',
        JSON.stringify(jobId),
        '->',
        JSON.stringify(seedEvent),
      );
      if (!seedEvent || !seedEvent.status) {
        // On recovery a null result means the job is genuinely gone; on a fresh
        // track() the row may not be queryable yet, so keep the optimistic state.
        if (pruneIfMissing) {
          this.removeMarker(jobId);
          this.jobs.delete(jobId);
          this.emitActiveJobs();
        }
        return;
      }
      this.upsert(seedEvent, label, minimized, jobType);
    } catch (error) {
      console.error('Error seeding job progress', error);
    }
  }

  /**
   * Apply an event through the monotonic guard, update the job's state, and
   * handle terminal transitions. Demuxed by jobId.
   */
  private upsert(
    event: JobProgressEvent,
    label: string,
    minimizedSeed?: boolean,
    jobTypeFallback?: string,
  ): void {
    const subject = this.ensureSubject(event.jobId);
    const current = subject.value;
    const incomingPercent = this.clampPercent(event.percent);
    const terminal = this.isTerminal(event.status);

    // Monotonic guard: never move the bar backward (terminal events always apply).
    if (!terminal && current.jobId && incomingPercent < current.percent) {
      return;
    }

    const next = this.buildNextState(current, event, terminal, incomingPercent, {
      label,
      minimizedSeed,
      jobTypeFallback,
    });
    subject.next(next);
    this.emitActiveJobs();

    if (terminal) {
      this.onTerminal(next, event);
    }
  }

  /**
   * Merge an incoming event onto the current state, preserving presentation and
   * applying terminal/fallback rules. Pure; extracted from upsert to keep each
   * function's branch count low.
   */
  private buildNextState(
    current: JobProgressState,
    event: JobProgressEvent,
    terminal: boolean,
    incomingPercent: number,
    seed: { label: string; minimizedSeed?: boolean; jobTypeFallback?: string },
  ): JobProgressState {
    return {
      jobId: event.jobId,
      jobType: event.jobType ?? current.jobType ?? seed.jobTypeFallback ?? '',
      label: seed.label || current.label,
      percent: terminal ? 100 : incomingPercent,
      phase: event.phase ?? current.phase ?? JOB_PROGRESS_DEFAULT_PHASE,
      status: event.status ?? 'InProgress',
      // Preserve current presentation; a seed can restore a minimized reload state.
      minimized: this.resolveMinimized(current, seed.minimizedSeed),
      message: event.error?.message ?? event.message ?? current.message ?? null,
    };
  }

  /** A live update keeps the current presentation; a fresh seed can restore it. */
  private resolveMinimized(
    current: JobProgressState,
    minimizedSeed?: boolean,
  ): boolean {
    if (current.percent > 0) {
      return current.minimized;
    }
    return minimizedSeed ?? current.minimized;
  }

  private onTerminal(state: JobProgressState, event: JobProgressEvent): void {
    this.removeMarker(state.jobId);
    this.cancelReseed(state.jobId);
    this.cancelWatchdog(state.jobId);
    this.liveJobs.delete(state.jobId);

    if (state.jobType === JOB_TYPE_PC_RESET) {
      // SYRC-7421: the reset reports its outcome in a dialog (app shell, on terminal$) per the design,
      // so no toastr here — that would announce the same result twice.
      this.terminalSubject.next(state);
      this.jobs.get(state.jobId)?.complete();
      this.jobs.delete(state.jobId);
      this.emitActiveJobs();
      return;
    }

    if (this.equalsIgnoreCase(state.status, JOB_PROGRESS_STATUS_SUCCESS)) {
      // Prefer the server-provided completion summary (the worker's
      // progress.success(summary) message). Fall back to the generic label-based
      // text only when the terminal event carries no message.
      this.toastr.showSuccess(
        event.message || JOB_PROGRESS_TOAST_SUCCESS(state.label),
        TOASTER_SUCCESS,
      );
    } else {
      const detail = event.error?.message || event.message;
      this.toastr.showError(
        detail || JOB_PROGRESS_TOAST_FAILED(state.label),
        ERROR,
      );
    }

    this.terminalSubject.next(state);
    // Complete + prune this job's stream; consumers got the toast + terminal$.
    const subject = this.jobs.get(state.jobId);
    subject?.complete();
    this.jobs.delete(state.jobId);
    this.emitActiveJobs();
  }

  private emitActiveJobs(): void {
    this.activeJobsSubject.next([...this.jobs.values()].map((s) => s.value));
  }

  private ensureSubject(jobId: string): BehaviorSubject<JobProgressState> {
    let subject = this.jobs.get(jobId);
    if (!subject) {
      subject = new BehaviorSubject<JobProgressState>({
        jobId,
        jobType: '',
        label: '',
        percent: 0,
        phase: JOB_PROGRESS_DEFAULT_PHASE,
        status: 'InProgress',
        minimized: false,
      });
      this.jobs.set(jobId, subject);
    }
    return subject;
  }

  private isTerminal(status: string | null | undefined): boolean {
    return (
      this.equalsIgnoreCase(status, JOB_PROGRESS_STATUS_SUCCESS) ||
      this.equalsIgnoreCase(status, JOB_PROGRESS_STATUS_FAILED)
    );
  }

  private equalsIgnoreCase(a: string | null | undefined, b: string): boolean {
    return (a ?? '').toLowerCase() === b.toLowerCase();
  }

  private clampPercent(percent: number | null | undefined): number {
    const value = typeof percent === 'number' ? percent : 0;
    return Math.min(100, Math.max(0, Math.round(value)));
  }

  private getIdToken(): string {
    return this.store.selectSnapshot(AuthenticateUserState.getIdToken) ?? '';
  }

  // SYRC-7180: the onJobProgress channel is keyed on the SYRC user id — the caller's
  // token identity, which is what AppSync authorizes the subscription against (every
  // other status subscription in the app subscribes with this id). The delete
  // mutation sends this same SYRC id as input.userId, and the worker publishes
  // progress on it. NOTE: this is deliberately NOT the CorView id — that id is only
  // the audit `deletedBy`; subscribing with it fails the connect (UnauthorizedException).
  private getUserId(): string {
    return (
      this.store.selectSnapshot(AuthenticateUserState.getSyrcUserId) ?? ''
    );
  }

  private teardownSubscription(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  // --- reload-survival markers (localStorage, one entry per active job) ---
  // ponytail: per-browser only. Upgrade path: the persisted job rows let you list
  // active jobs server-side for cross-device recovery if ever needed.
  private readMarkers(): ActiveJobMarker[] {
    try {
      const raw = localStorage.getItem(JOB_PROGRESS_ACTIVE_JOBS_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as ActiveJobMarker[];
      return Array.isArray(parsed) ? parsed.filter((m) => m?.jobId) : [];
    } catch {
      return [];
    }
  }

  private writeMarker(marker: ActiveJobMarker): void {
    const markers = this.readMarkers().filter((m) => m.jobId !== marker.jobId);
    markers.push(marker);
    this.persistMarkers(markers);
  }

  private updateMarker(
    jobId: string,
    fn: (m: ActiveJobMarker) => ActiveJobMarker,
  ): void {
    const markers = this.readMarkers().map((m) =>
      m.jobId === jobId ? fn(m) : m,
    );
    this.persistMarkers(markers);
  }

  private removeMarker(jobId: string): void {
    this.persistMarkers(this.readMarkers().filter((m) => m.jobId !== jobId));
  }

  private persistMarkers(markers: ActiveJobMarker[]): void {
    try {
      if (markers.length === 0) {
        localStorage.removeItem(JOB_PROGRESS_ACTIVE_JOBS_KEY);
      } else {
        localStorage.setItem(
          JOB_PROGRESS_ACTIVE_JOBS_KEY,
          JSON.stringify(markers),
        );
      }
    } catch (error) {
      console.warn('Failed to persist active job markers', error);
    }
  }
}
